import { html, nothing, type PropertyValues, type TemplateResult } from "lit";
import { property, query, queryAll, state } from "lit/decorators.js";
import { map } from "lit/directives/map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { UiElement, type ZbiChangeEvent } from "@zebrabi/ui-components";
import type { FieldRole } from "@zebrabi/office-types";
import type { BucketDefinition, DataSource, Field, Placement, RoleChange } from "../definitions";
import { applyRoleChanges, defaultBucketFor, insertionIndex, resolveFieldDrop, zoneFromY, type DropZone } from "../dropRules";
import createEvent from "../events/create-event";
import type { FieldsPaneEditDataSourceEvent } from "../events/fields-pane-edit-data-source";
import type { FieldsPaneReorderEvent } from "../events/fields-pane-reorder";
import type { FieldsPaneRolesChangeEvent } from "../events/fields-pane-roles-change";
import type { FieldsPaneBucket } from "../field-bucket/field-bucket.component";
import type { FieldsPaneField } from "../field/field.component";
import tokens from "../tokens";
import styles from "./fields-pane.styles";

/** How far past a bucket's zone a release still lands in it; the gaps between buckets are wider, so no bucket steals from another */
const DROP_TOLERANCE = 12;

/** How far the pointer has to travel from the press for a release to count as a drop; closer is a click, which never moves the field */
const MIN_DRAG_DISTANCE = 8;

interface Drag {
    field: Field;
    element: FieldsPaneField;
    /** The copy that follows the pointer; the field itself stays in place, dimmed, so the layout never shifts */
    ghost: FieldsPaneField;
    /** Where the field may land, decided once when the drag starts */
    targets: Placement[];
    /** The pointer's y at the press, to tell a click from a drop */
    pressY: number;
}

/** The slot a dragged field would take in its own bucket, when that differs from where it is */
interface Insertion {
    role: FieldRole;
    index: number;
}

/** What the buckets show while a drag runs: the targets, the one under the pointer, and the reorder slot */
interface DragFeedback {
    origin: Placement;
    targets: Placement[];
    /** The target under the pointer; `undefined` while the pointer is over none */
    active: Placement | undefined;
    insertion: Insertion | null;
}

/**
 * The Data fields pane: a bucket per definition with its fields inside, the Data source block above them, the Fields
 * list above the buckets when the host turns it on, and the drag between all of those. A gesture ends in one event;
 * the host commits it and passes the new fields back in.
 */
export class FieldsPane extends UiElement {
    static styles = [tokens, styles];

    @property({ attribute: false })
    public buckets: BucketDefinition[] = [];

    @property({ attribute: false })
    public fields: Field[] = [];

    /** `undefined` hides the Data source block; `null` shows it without a source and without the Edit button */
    @property({ attribute: false })
    public dataSource?: DataSource | null;

    /**
     * Shows the Fields list above the buckets: every field in the order given, with a checkbox ticked while the field
     * sits in a bucket. Fields drag between the list and the buckets, and a movable bucket field gets × to leave its bucket.
     */
    @property({ type: Boolean, attribute: "fields-list" })
    public fieldsList = false;

    /** Shows where a dragged field can land: the buckets that take it framed, the one under the pointer solid, the others faded */
    @property({ type: Boolean, attribute: "drag-feedback" })
    public dragFeedback = false;

    /** Shows the Fields list without letting the user change the assignment through it. */
    @property({ type: Boolean, attribute: "fields-list-read-only" })
    public fieldsListReadOnly = false;

    private drag: Drag | null = null;

    @state()
    private feedback: DragFeedback | null = null;

    @query(".inner-form")
    private readonly innerForm?: HTMLElement;

    @queryAll("fields-pane-bucket")
    private readonly bucketNodes?: NodeListOf<FieldsPaneBucket>;

    /** Cancels a running drag when the host modifies the buckets or the fields it started on */
    protected shouldUpdate(changedProperties: PropertyValues<this>): boolean {
        if (this.drag && (changedProperties.has("fields") || changedProperties.has("buckets"))) {
            this.cancelDrag();
        }
        return true;
    }

    /** A pane removed mid-drag never gets the release: the drag ends with it */
    public override disconnectedCallback(): void {
        this.cancelDrag();
        super.disconnectedCallback();
    }

    protected render(): TemplateResult {
        return html`
            ${this.renderDataSource()}
            <div class="inner-form">${this.renderFieldsList()} ${map(this.buckets, this.renderBucket)}</div>
        `;
    }

    private renderDataSource(): TemplateResult | typeof nothing {
        if (this.dataSource === undefined) {
            return nothing;
        }
        const editButton = this.dataSource
            ? html`<button class="edit-data-source-btn" aria-label="Edit data source" @click=${this.handleEditClick}></button>`
            : nothing;
        return html`
            <div class="edit-source-data">
                <p class="data-source-label">Data source</p>
                <div>
                    <p class="data-source">${describeDataSource(this.dataSource)}</p>
                    ${editButton}
                </div>
            </div>
        `;
    }

    private renderBucket = (bucket: BucketDefinition): TemplateResult => {
        return html`
            <fields-pane-bucket
                .label=${bucket.label}
                .dropTarget=${this.isDropTarget(bucket.role)}
                .dropActive=${this.isDropActive(bucket.role)}
                .faded=${this.isFaded(bucket.role)}
                data-role=${bucket.role}
            >
                ${map(this.fieldsIn(bucket.role), this.renderField)}
            </fields-pane-bucket>
        `;
    };

    private renderField = (field: Field): TemplateResult => {
        return html`
            <fields-pane-field
                .label=${field.label}
                .movable=${field.movable}
                .removable=${this.listEditable() && field.movable}
                .insertion=${this.insertionFor(field)}
                title=${ifDefined(field.tooltip)}
                data-id=${field.id}
                @pointerdown=${this.handlePointerDown}
                @pointermove=${this.handlePointerMove}
                @pointerup=${this.handlePointerUp}
                @pointercancel=${this.handlePointerCancel}
                @lostpointercapture=${this.handlePointerCancel}
                @zbi-click=${this.handleRemove}
            ></fields-pane-field>
        `;
    };

    /** The list is a bucket without a role: a drop on it takes the field out of its bucket */
    private renderFieldsList(): TemplateResult | typeof nothing {
        if (!this.fieldsList) {
            return nothing;
        }
        return html`
            <fields-pane-bucket
                class="fields-list"
                label="Fields"
                list
                .dropTarget=${this.isDropTarget(null)}
                .dropActive=${this.isDropActive(null)}
                .faded=${this.isFaded(null)}
            >
                ${map(this.fields, this.renderListEntry)}
            </fields-pane-bucket>
        `;
    }

    /** With a Values entry in the list, the measures it stands for are indented under it */
    private renderListEntry = (field: Field): TemplateResult => {
        const nested = field.kind === "measure" && this.fields.some((candidate) => candidate.kind === "values");
        return html`
            <fields-pane-field
                .label=${field.label}
                .kind=${field.kind}
                .movable=${this.listEditable() && field.movable}
                .checked=${field.role !== null}
                .nested=${nested}
                checkbox
                title=${ifDefined(field.tooltip)}
                data-id=${field.id}
                @pointerdown=${this.handlePointerDown}
                @pointermove=${this.handlePointerMove}
                @pointerup=${this.handlePointerUp}
                @pointercancel=${this.handlePointerCancel}
                @lostpointercapture=${this.handlePointerCancel}
                @zbi-change=${this.handleToggle}
            ></fields-pane-field>
        `;
    };

    /** Whether the list is shown and lets the user change the assignment */
    private listEditable(): boolean {
        return this.fieldsList && !this.fieldsListReadOnly;
    }

    private isDropTarget(placement: Placement): boolean {
        return this.feedback?.targets.includes(placement) ?? false;
    }

    private isDropActive(placement: Placement): boolean {
        return this.isDropTarget(placement) && this.feedback?.active === placement;
    }

    /** Everything that cannot take the field fades back, except the bucket the field is dragged out of */
    private isFaded(placement: Placement): boolean {
        const feedback = this.feedback;
        return feedback !== null && !feedback.targets.includes(placement) && feedback.origin !== placement;
    }

    /** The field that carries the insertion line: the one at the slot, or the last one when the slot is below it */
    private insertionFor(field: Field): "before" | "after" | undefined {
        const insertion = this.feedback?.insertion;
        const dragged = this.drag?.field;
        if (!insertion || !dragged || field.role !== insertion.role || field.id === dragged.id) {
            return undefined;
        }
        const others = this.fieldsIn(insertion.role).filter((candidate) => candidate.id !== dragged.id);
        if (insertion.index < others.length) {
            return others[insertion.index] === field ? "before" : undefined;
        }
        return others.at(-1) === field ? "after" : undefined;
    }

    private fieldsIn(role: FieldRole): Field[] {
        return this.fields.filter((field) => field.role === role);
    }

    private fieldOf(element: EventTarget | null): Field | undefined {
        const id = (element as FieldsPaneField | null)?.dataset.id;
        return this.fields.find((candidate) => candidate.id === id);
    }

    private handleEditClick(event: MouseEvent): void {
        if (event.detail > 1) {
            // a double click would open the dialog twice
            return;
        }
        this.dispatchEvent(createEvent<FieldsPaneEditDataSourceEvent>("fields-pane-edit-data-source"));
    }

    /** A tick puts the field in the first bucket of its kind with room; an untick takes it out of its bucket */
    private handleToggle(event: ZbiChangeEvent<boolean>): void {
        const field = this.fieldOf(event.currentTarget);
        if (!field?.movable || !this.listEditable()) {
            return;
        }
        const role = event.detail.value ? defaultBucketFor(field, this.buckets, this.fields) : null;
        if (role !== field.role) {
            this.emitRolesChange([{ fieldId: field.id, role }]);
        }
    }

    private handleRemove(event: Event): void {
        const field = this.fieldOf(event.currentTarget);
        if (field?.movable && field.role !== null && this.listEditable()) {
            this.emitRolesChange([{ fieldId: field.id, role: null }]);
        }
    }

    private handlePointerDown(event: PointerEvent): void {
        if (this.drag || event.button !== 0 || event.ctrlKey) {
            // early exit on an active drag and on anything but a plain left click
            return;
        }
        const element = event.currentTarget as FieldsPaneField;
        const field = this.fieldOf(element);
        if (!field?.movable || !element.movable) {
            // a read-only list entry renders as fixed even when its field is movable from a bucket
            return;
        }
        const form = this.innerForm;
        if (!form) {
            return;
        }
        // keeps the move and up events on the field while the pointer is outside it
        element.setPointerCapture?.(event.pointerId);
        const ghost = document.createElement("fields-pane-field");
        ghost.label = field.label;
        ghost.classList.add("ghost");
        form.appendChild(ghost);
        const targets = this.dropTargetsFor(field);
        this.drag = { field, element, ghost, targets, pressY: event.clientY };
        this.feedback = this.dragFeedback ? { origin: field.role, targets, active: undefined, insertion: null } : null;
        element.toggleAttribute("dragging", true);
        this.followPointer(event);
    }

    /** The placements a drop would change something for: the buckets, and the list when the host lets it take a field */
    private dropTargetsFor(field: Field): Placement[] {
        const placements: Placement[] = this.buckets.map((bucket) => bucket.role);
        if (this.listEditable()) {
            placements.push(null);
        }
        return placements.filter((placement) => resolveFieldDrop(field, placement, this.buckets, this.fields).length > 0);
    }

    private handlePointerMove(event: PointerEvent): void {
        if (this.drag?.element === event.currentTarget) {
            this.followPointer(event);
        }
    }

    /** A release near the press is a click: the drag ends and nothing is dropped, so a click in the list never unassigns */
    private handlePointerUp(event: PointerEvent): void {
        const drag = this.drag;
        if (!drag || drag.element !== event.currentTarget) {
            return;
        }
        this.cancelDrag();
        if (Math.abs(event.clientY - drag.pressY) >= MIN_DRAG_DISTANCE) {
            this.emitDropOutcome(drag, event.clientY);
        }
    }

    /** The browser took the pointer (a touch pan, a lost window, a field removed from the DOM): nothing is dropped */
    private handlePointerCancel(event: PointerEvent): void {
        if (this.drag?.element === event.currentTarget) {
            this.cancelDrag();
        }
    }

    private cancelDrag(): void {
        const drag = this.drag;
        if (!drag) {
            return;
        }
        this.drag = null;
        this.feedback = null;
        drag.ghost.remove();
        drag.element.removeAttribute("dragging");
    }

    /** The ghost follows the pointer vertically, inside the form it is positioned against; the buckets show where it may land */
    private followPointer(event: PointerEvent): void {
        const drag = this.drag;
        const form = this.innerForm;
        if (!drag || !form) {
            return;
        }
        const top = event.clientY - form.getBoundingClientRect().top - drag.element.getBoundingClientRect().height / 2;
        drag.ghost.style.top = `${top}px`;
        this.updateFeedback(drag, event.clientY);
    }

    private updateFeedback(drag: Drag, pointerY: number): void {
        const feedback = this.feedback;
        if (!feedback) {
            return;
        }
        const zone = this.zoneAt(pointerY);
        const active = zone && drag.targets.includes(zone.role) ? zone.role : undefined;
        const insertion =
            zone && zone.role === drag.field.role && drag.field.role !== null
                ? this.reorderSlot(drag.field, drag.field.role, pointerY)
                : null;
        if (active !== feedback.active || insertion?.index !== feedback.insertion?.index) {
            this.feedback = { ...feedback, active, insertion };
        }
    }

    private zoneAt(pointerY: number): DropZone | null {
        const zones = this.bucketElements().map(
            (bucket): DropZone => ({ role: (bucket.dataset.role as FieldRole | undefined) ?? null, box: bucket.zoneRect() })
        );
        return zoneFromY(pointerY, zones, DROP_TOLERANCE);
    }

    /** A release lands only where the drag could go: on the field's own bucket for a reorder, or on one of its targets */
    private emitDropOutcome(drag: Drag, pointerY: number): void {
        const field = drag.field;
        const zone = this.zoneAt(pointerY);
        if (zone === null) {
            return;
        }
        if (zone.role === field.role) {
            if (field.role !== null) {
                this.emitReorder(field, field.role, pointerY);
            }
            return;
        }
        if (!drag.targets.includes(zone.role)) {
            return;
        }
        this.emitRolesChange(resolveFieldDrop(field, zone.role, this.buckets, this.fields));
    }

    /** The slot a field dragged inside its own bucket would take, when the bucket reorders and the slot is not its own */
    private reorderSlot(field: Field, role: FieldRole, pointerY: number): Insertion | null {
        const bucket = this.buckets.find((candidate) => candidate.role === role);
        if (!bucket?.reorderable) {
            return null;
        }
        const others = this.fieldElementsIn(role).filter((element) => element.dataset.id !== field.id);
        const index = insertionIndex(
            pointerY,
            others.map((element) => element.getBoundingClientRect())
        );
        return index === this.fieldsIn(role).indexOf(field) ? null : { role, index };
    }

    private emitReorder(field: Field, role: FieldRole, pointerY: number): void {
        const slot = this.reorderSlot(field, role, pointerY);
        if (slot) {
            this.dispatchEvent(createEvent<FieldsPaneReorderEvent>("fields-pane-reorder", { fieldId: field.id, role, index: slot.index }));
        }
    }

    private emitRolesChange(changes: RoleChange[]): void {
        if (changes.length > 0) {
            const fields = applyRoleChanges(this.fields, changes);
            this.dispatchEvent(createEvent<FieldsPaneRolesChangeEvent>("fields-pane-roles-change", { changes, fields }));
        }
    }

    private bucketElements(): FieldsPaneBucket[] {
        return Array.from(this.bucketNodes ?? []);
    }

    private fieldElementsIn(role: FieldRole): FieldsPaneField[] {
        const bucket = this.bucketElements().find((candidate) => candidate.dataset.role === (role as string));
        return bucket ? Array.from(bucket.querySelectorAll<FieldsPaneField>("fields-pane-field")) : [];
    }
}

function describeDataSource(dataSource: DataSource | null): string {
    if (dataSource?.range) {
        return `Range: ${dataSource.range}`;
    }
    if (dataSource?.table) {
        return `Excel table: ${dataSource.table}`;
    }
    if (dataSource?.pivotTable) {
        return `Pivot table: ${dataSource.pivotTable}`;
    }
    return "";
}
